"use client";
import React, { useEffect, useState, useContext } from "react";
import "./index.scss";
import "@/app/style/speedpro.scss";
import { useLanguage } from "@/app/utils/LanguageContext";
import { getSearchParams, setRouting, getAssetPrefix, getCmsDomain, updateMetaTag } from "@/app/utils/Common";
import { getSpeedguideByRaceNo } from "@/app/utils/request/requestList";
import { PageContext } from "./PageContext";
import { pageSet } from "@/app/utils/pageSet";

const SpeedGuide = (props: any) => {
    const { state: lang } = useLanguage();
    const { t } = lang;
    const assetPrefix = getAssetPrefix();
    const raceData = useContext(PageContext);
    const { raceno } = typeof window !== 'undefined' && getSearchParams(new URLSearchParams(window?.location?.search));
    const [speedguideData, setSpeedguideData] = useState<any>(null);
    const [excelLink, setExcelLink] = useState<string>();
    // user inputs for estimated enery
    const [userInputs, setUserInputs] = useState<Record<string, string>>({});
    // last sorted inputs
    const [lastSortedInputs, setLastSortedInputs] = useState<Record<string, string>>({});

    // initial sort-- speedproenergydifference
    const [sortConfig, setSortConfig] = useState({
        key: 'speedproenergydifference',
        direction: 'dec'
    }); 

    const handleTutorialPop = (e: any) => {
        var features = 'menubar=no,height=600,width=600,scrollbars=yes'
        window.open("./tutorial", t("UserGuide"), features);
    }

    useEffect(() => {
        // reset sortConfig to default
        setSortConfig({
            key: 'speedproenergydifference',
            direction: 'dec'
        });

        if (raceno) {
            getSpeedguideByRaceNo(raceno).then((res:any) => {
                setSpeedguideData(res[lang.langCode]);
            })
        } else {
            setRouting(undefined, { "raceno": "1" }); //default raceno = 1
        }
    }, [raceno]);
    
    useEffect(() => {
        updateMetaTag(lang.langCode, pageSet.speedguide);

        if (raceData?.[lang.langType == "chinese" ? "excelch" : "excelen"]) {
            const path = `${getCmsDomain()}/-/media/Sites/JCRW/SpeedPro/current/`;
            setExcelLink(`${path}${raceData?.[lang.langType == "chinese" ? "excelch" : "excelen"]}`);
        }
    }, []);
  
    // initial with userInput
    useEffect(() => {
        setUserInputs({});
        setLastSortedInputs({});       

        if (sortConfig.key === 'yourEstimatedEnergy' || sortConfig.key === 'yourEstimatedEnergyDifference') {
            setLastSortedInputs({...userInputs});
        }
    }, [raceno]);    
 
    const validRunners = React.useMemo(() => {
        if (!speedguideData || !speedguideData.SpeedPRO || !Array.isArray(speedguideData.SpeedPRO) || speedguideData.SpeedPRO.length === 0) {
            return [];
        }
        
        return speedguideData.SpeedPRO;
    }, [speedguideData]);
    // get trend className
    const getTrendClassName = (trend: string | number): string => {
        if (trend === undefined || trend === null || String(trend).trim() === '') {
          return '';
        }
        
        const value = Number(trend);
        
        if (value === 0) {
          return 'thumb_down';
        } else if (value === 1) {
          return 'formGuide_1up';
        } else if (value === 2) {
          return 'formGuide_2up';
        } else if (value === 3) {
          return 'formGuide_3up';
        }
        return '';
    };

    // handle user input
    const handleUserInput = (runnerNumber: string, value: string) => {
        // remove Spaces
        const cleanValue = value.replace(/\s+/g, '');
        // check if valid
        if (cleanValue && isNaN(Number(cleanValue))) {
            alert(lang.langCode == "zh-hk"  ? '只限輸入數字' : 'Please enter a valid number');
            return;
        }
         
        setUserInputs({
            ...userInputs,
            [runnerNumber]: cleanValue
        });
    };

    // format speedproenergydifference
    const formatSpeedproenergydifference = (energydifference: string) => {
        if (!energydifference) return '-';
        
        const diffEnergy = parseFloat(energydifference);

        if (isNaN(diffEnergy) || isNaN(diffEnergy)) return `<span style="font-weight: bold">${energydifference}</span>`;;
                
        if (diffEnergy === 0) return `<span style="font-weight: bold">${energydifference}</span>`;;;
        if (diffEnergy > 0) return `<span style="font-size:12px; color:#3879d9;font-weight: bold">${energydifference}</span>`;
        return `<span style="font-size:12px; color:#c80000;font-weight: bold">${energydifference}</span>`;
    };
    
    // calculate difference（compared with energy Required）
    const calculateDifference = (energyRequired: string, userEnergy: string) => {
        if (!userEnergy) return '-';
        
        const requiredEnergy = parseFloat(energyRequired);
        const userEnergyValue = parseFloat(userEnergy);
        
        if (isNaN(requiredEnergy) || isNaN(userEnergyValue)) return '-';
        
        const diff = userEnergyValue - requiredEnergy ;
        
        if (diff === 0) return `<b>0</b>`;
        if (diff > 0) return `<span style="color:#3879d9; font-weight: bold; font-size: 12px">+${diff}</span>`;
        return `<span style="color:#c80000; font-weight: bold; font-size: 12px">-${Math.abs(diff)}</span>`;
    };

    // check if sortable
    const isSortableColumn = (key: string): boolean => {
        const sortableColumns = [
            'runnernumber',            
            'draw',                    
            'energyrequired',          
            'speedproenergy',       
            'speedproenergydifference',  
            'yourEstimatedEnergy',       
            'yourEstimatedEnergyDifference',  
        ];
        return sortableColumns.includes(key);
    };

    // click sort
    const handleClickSort = (key: string) => {
 
        if (!isSortableColumn(key)) return;
        
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'dec';
        }
        
        // sync value
        if (key === 'yourEstimatedEnergy' || key === 'yourEstimatedEnergyDifference') {
            setLastSortedInputs({...userInputs});
        }
                
        setSortConfig({ key, direction });
    };

    // sorted list
    const sortedRunners = [...validRunners].sort((a, b) => {
        if (!sortConfig.key) {
            return parseInt(a.runnernumber || '0') - parseInt(b.runnernumber || '0');
        }

        if (sortConfig.key === 'speedproenergy') {
            const aValue = parseFloat(a.speedproenergy || '0');
            const bValue = parseFloat(b.speedproenergy || '0');

            if (isNaN(aValue) && isNaN(bValue)) return 0;
            // If is NaN, it goes to the end (return 1)
            if (isNaN(aValue)) return 1;
            if (isNaN(bValue)) return -1;

            return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }

        if (sortConfig.key === 'speedproenergydifference') {
            const aValue = parseFloat(a.speedproenergydifference || '0');
            const bValue = parseFloat(b.speedproenergydifference || '0');

            if (isNaN(aValue) && isNaN(bValue)) return 0;
            // If is NaN, it goes to the end (return 1)
            if (isNaN(aValue)) return 1;
            if (isNaN(bValue)) return -1;

            return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }

        if (sortConfig.key === 'yourEstimatedEnergy') {
            // sort lastSortedInput
            const aValue = lastSortedInputs[a.runnernumber] ? parseFloat(lastSortedInputs[a.runnernumber]) : NaN;
            const bValue = lastSortedInputs[b.runnernumber] ? parseFloat(lastSortedInputs[b.runnernumber]) : NaN;

            if (isNaN(aValue) && isNaN(bValue)) return 0;
            if (isNaN(aValue)) return 1;
            if (isNaN(bValue)) return -1;

            return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }

        if (sortConfig.key === 'yourEstimatedEnergyDifference') {
            // calculate the difference between energyRequired and yourEstimatedEnergyDifference (lastSortedInput)  
            const aRequired = parseFloat(a.energyrequired || '0');
            const bRequired = parseFloat(b.energyrequired || '0');
            const aInput = lastSortedInputs[a.runnernumber] ? parseFloat(lastSortedInputs[a.runnernumber]) : NaN;
            const bInput = lastSortedInputs[b.runnernumber] ? parseFloat(lastSortedInputs[b.runnernumber]) : NaN;

            const aDiff = !isNaN(aRequired) && !isNaN(aInput) ? aInput - aRequired : NaN;
            const bDiff = !isNaN(bRequired) && !isNaN(bInput) ? bInput - bRequired : NaN;

            if (isNaN(aDiff) && isNaN(bDiff)) return 0;
            if (isNaN(aDiff)) return 1;
            if (isNaN(bDiff)) return -1;
            
            return sortConfig.direction === 'asc' ? aDiff - bDiff : bDiff - aDiff;
        }

        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        // handle different types of value
        if (typeof aValue === 'string' && typeof bValue === 'string') {
            const numA = parseFloat(aValue);
            const numB = parseFloat(bValue);

            if (!isNaN(numA) && !isNaN(numB)) {
                return sortConfig.direction === 'asc' ? numA - numB : numB - numA;
            }

            return sortConfig.direction === 'asc'
                ? aValue.localeCompare(bValue)
                : bValue.localeCompare(aValue);
        }

        // default sort by race no 
        return parseInt(a.runnernumber || '0') - parseInt(b.runnernumber || '0');
    });

    const displayText = (obj: any) => {
        if (obj) {
            if (typeof obj === 'object') {
                if (!obj['energy']) {
                    obj['energy'] = ''
                }
                if (!obj['racecourse']) {
                    obj['racecourse'] = ''
                }
                if (!obj['distance']) {
                    obj['distance'] = ''
                }
                if (!obj['course']) {
                    obj['course'] = ''
                }
                if (!obj['energy'] && !obj['racecourse'] && !obj['distance'] && !obj['course']) {
                    return '-'
                }
                else {
                    return <>
                        {obj.energy}
                        <br />
                        {obj?.["racecourse" + (lang.langCode == "zh-hk" ? "_chi" : "_eng")]}&nbsp;{obj.distance}
                        <br />
                        {obj.course}
                    </>
                }
            }
            else {
                return obj
            }
        }
        else {
            return '-'
        }
    } 

    return (
        <div>
            <div className="race-main">
                <div className="race-detail">
                    {
                        speedguideData && speedguideData?.RaceMap ?
                            <a href={`./formguide?raceno=${raceno}`}>
                                <img className={`has-map speedguide-map raceMap-${speedguideData.RaceNo}`} src={speedguideData?.RaceMap}  alt="Speed Map Image"/>
                            </a> :
                            <div className="no-map"><p>{t("NoMapRemark")}</p></div>
                    }
                </div>
                <a className="guide" onClick={handleTutorialPop}><img className="tutorial-btn" src={`${assetPrefix}/speedpro/tutorial_${lang.langType}.gif`} /></a>
                {
                    excelLink ?
                        <a className="download" href={excelLink} dangerouslySetInnerHTML={{ "__html": t("DownloadRating") }}>
                        </a> : <></>
                }
            </div>
            <div className="dataTables">
                <table className="datatable dt-9" sort-index="13">
                    <thead>
                        <tr>
                            <th 
                                className={sortConfig.key === 'runnernumber' ? `sort-${sortConfig.direction}` : 'sort'}
                                onClick={() => handleClickSort('runnernumber')}
                            >
                                <div dangerouslySetInnerHTML={{ __html: t('RunnerNumber') }}></div>
                            </th>
                            <th><div dangerouslySetInnerHTML={{ __html: t('HorseName') }}></div></th>
                            <th 
                                className={sortConfig.key === 'draw' ? `sort-${sortConfig.direction}` : 'sort'}
                                onClick={() => handleClickSort('draw')}
                            >
                                <div dangerouslySetInnerHTML={{ __html: t('Draw') }}></div>
                            </th>
                            <th 
                                className={sortConfig.key === 'energyrequired' ? `sort-${sortConfig.direction}` : 'sort'}
                                onClick={() => handleClickSort('energyrequired')}
                            >
                                <div dangerouslySetInnerHTML={{ __html: t('EnergyRequired') }}></div>
                            </th>
                            <th><div dangerouslySetInnerHTML={{ __html: t('5thLastRun') }}></div></th>
                            <th><div dangerouslySetInnerHTML={{ __html: t('4thLastRun') }}></div></th>
                            <th><div dangerouslySetInnerHTML={{ __html: t('3rdLastRun') }}></div></th>
                            <th><div dangerouslySetInnerHTML={{ __html: t('2ndLastRun') }}></div></th>
                            <th><div dangerouslySetInnerHTML={{ __html: t('LastRun') }}></div></th>
                            <th><div dangerouslySetInnerHTML={{ __html: t('BestLast12Months') }}></div></th>
                            <th><div dangerouslySetInnerHTML={{ __html: t('BestAtDistanceLast12Months') }}></div></th>
                            <th><div dangerouslySetInnerHTML={{ __html: t('FitnessRatings') }}></div></th>
                            <th 
                                className={sortConfig.key === 'speedproenergy' ? `sort-${sortConfig.direction}` : 'sort'}
                                onClick={() => handleClickSort('speedproenergy')}
                            >
                                <div dangerouslySetInnerHTML={{ __html: t('SpeedPROEnergy') }}></div>
                            </th>
                            <th 
                                className={sortConfig.key === 'speedproenergydifference' ? `sort-${sortConfig.direction}` : 'sort'}
                                onClick={() => handleClickSort('speedproenergydifference')}
                            >
                                <div dangerouslySetInnerHTML={{ __html: t('SpeedPROEnergyDifference') }}></div>
                            </th>
                            <th 
                                className={sortConfig.key === 'yourEstimatedEnergy' ? `sort-${sortConfig.direction}` : 'sort'}
                                onClick={() => handleClickSort('yourEstimatedEnergy')}
                            >
                                <div dangerouslySetInnerHTML={{ __html: t('YourEstimatedEnergy') }}></div>
                            </th>
                            <th 
                                className={sortConfig.key === 'yourEstimatedEnergyDifference' ? `sort-${sortConfig.direction}` : 'sort'}
                                onClick={() => handleClickSort('yourEstimatedEnergyDifference')}
                            >
                                <div dangerouslySetInnerHTML={{ __html: t('YourEstimatedEnergyDifference') }}></div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedRunners.map((item: any) => (
                            <tr key={item.runnernumber}>
                                <td className={sortConfig.key === 'runnernumber' ? 'bgYellow' : ''}>{displayText(item.runnernumber)}</td>
                                <td>{item?.["name"+ (lang.langCode == "zh-hk" ? "_chi" : "_eng")]}</td>
                                <td className={sortConfig.key === 'draw' ? 'bgYellow' : ''}>{displayText(item.draw)}</td>
                                <td className={sortConfig.key === 'energyrequired' ? 'bgYellow' : ''}>{displayText(item.energyrequired)}</td>
                                <td>
                                    {displayText(item.lastrun_5th)}
                                </td>
                                <td>
                                    {displayText(item.lastrun_4th)}
                                </td>
                                <td>
                                    {displayText(item.lastrun_3rd)}
                                </td>
                                <td>
                                    {displayText(item.lastrun_2nd)}
                                </td>
                                <td>
                                    {displayText(item.lastrun)}
                                </td>
                                <td>
                                    {displayText(item.bestlast12months)}
                                </td>
                                <td>
                                    {displayText(item.bestatdistance)}
                                </td>
                                <td className="fitness-ratings-cell formGuide_up">
                                    {item.fitnessrating  && (
                                        <div className={`fitness-icon ${getTrendClassName(item.fitnessrating)}`} style={{ width: '100%', height: '100%' }}></div>
                                        )}
                                </td>
                                <td className={sortConfig.key === 'speedproenergy' ? 'bgYellow' : ''}>
                                    {displayText(item.speedproenergy)}
                                </td>
                                <td 
                                    className={sortConfig.key === 'speedproenergydifference' ? 'bgYellow' : ''}
                                    dangerouslySetInnerHTML={{ 
                                        __html: formatSpeedproenergydifference(
                                            item.speedproenergydifference || ''
                                        ) 
                                    }}
                                ></td>
                                <td className={sortConfig.key === 'yourEstimatedEnergy' ? 'bgYellow' : ''}>
                                    <input
                                        type="text"
                                        className="your-energy"
                                        data-in={item.runnernumber}
                                        data-val=""
                                        value={userInputs[item.runnernumber] || ''}
                                        onChange={(e) => handleUserInput(item.runnernumber, e.target.value)}
                                    />
                                </td>
                                <td 
                                    className={sortConfig.key === 'yourEstimatedEnergyDifference' ? 'sult bgYellow' : 'sult'}
                                    dangerouslySetInnerHTML={{ 
                                        __html: calculateDifference(
                                            item.energyrequired || '', 
                                        userInputs[item.runnernumber] || ''
                                        ) 
                                    }}
                                ></td>
                            </tr>
                        ))}
                    </tbody>
                </table>

            </div>
            {
                lang.langCode == "zh-hk" ?
                    <div className="remark">
                        <p>「速勢能量評估」及「狀態評級」於賽前一天中午發佈。</p>
                        <p>「場地狀況」於賽前一天中午發佈，詳情請參閱<a href="../../information/racecard" target="_blank">官方排位表</a>。</p>
                        <br/>
                        <p>速勢能量是由特約供應商製作，並由馬會向外界發放，供馬迷投注時作為參考。已盡一切努力務求有關參考資料盡可能準確，馬會就此並無責任。</p>
                        <p>上述資料只作參考用途，並不構成任何建議。</p>                        
                    </div>
                    :
                    <div className="remark">
                        <p>“SpeedPRO Energy” and “Fitness Ratings” are posted at Noon one day before race day.</p>
                        <p className="en">“Track going” is posted at Noon one day before race day, please refer to <a href="../../information/racecard" target="_blank">official Race Card page</a>.</p>
                        <br/>
                        <p>SpeedPRO is produced by external vendors and is not official Jockey Club information. Every effort is made to ensure the information is as accurate as possible, but the club assumes no responsibility for it. The above information is for reference only and does not constitute any advice.</p>
                    </div>
            }
        </div>
    )
}

export default SpeedGuide;